import { useCallback, useMemo, useState } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { yupResolver } from '@hookform/resolvers/yup';
import { GetCompanySearchResponse } from '@iwoca/lapi-client/edge';
import { GetApiIntroducerPortalDirectorSearchResponse } from '@iwoca/lapi-client/introducer_portal';
import { Button, Card, CheckBox, Message } from '@iwoca/orion';
import { useIntroducerContext } from 'contexts/IntroducerContext/IntroducerContext';
import { useAmplitude } from 'hooks/useAmplitude';
import { useForm } from 'react-hook-form';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { CompanyInputUK } from './CompanyInputUK/CompanyInputUK';
import { CriteriaItem } from './CriteriaItem/CriteriaItem';
import { DateOfBirthInput } from './DateOfBirthInput/DateOfBirthInput';
import { DirectorSearch } from './DirectorSearch/DirectorSearch';
import { useFormSubmit, useReintroductionSearch } from './NewSignupForm.hooks';
import styles from './NewSignupForm.module.css';
import { schema } from './NewSignUpForm.schema';
import { TNewSignupFormData } from './NewSignupForm.types';
import { ReintroducedSignupFields } from './ReintroducedSignupFields/ReintroducedSignupFields';
import { ReintroductionSearchStatus } from './ReintroductionSearchStatus/ReintroductionSearchStatus';

export const NewSignupForm = () => {
  const [selectedCompany, setSelectedCompany] =
    useState<GetCompanySearchResponse['data'][number]>();
  const [selectedPerson, setSelectedPerson] =
    useState<GetApiIntroducerPortalDirectorSearchResponse['data'][number]>();
  const { introducerId, splitTests } = useIntroducerContext();
  const { trackEvent } = useAmplitude();
  const { reintroducedPerson, isFetchingReintroductionSearch } =
    useReintroductionSearch(
      selectedCompany?.number,
      introducerId,
      splitTests?.sameIntroducerAccountReintroductions,
    );

  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<TNewSignupFormData>({ resolver: yupResolver(schema) });

  const { onSubmit, isSubmitting, signupError } = useFormSubmit({
    selectedCompany,
    selectedPerson,
    introducerId,
    reintroducedPerson,
  });

  const submitReintroductionSignup = async () => {
    const isValid = await trigger('privacyPolicyAgreement');
    if (isValid) {
      onSubmit();
    }
  };

  const setSelectedPersonWithDOB = (
    person:
      | GetApiIntroducerPortalDirectorSearchResponse['data'][number]
      | undefined,
  ) => {
    setSelectedPerson(person);
    setValue('dobMonth', person?.month_of_birth?.toString());
    setValue('dobYear', person?.year_of_birth?.toString());
  };

  const setCompanyWithTracking = (
    company: GetCompanySearchResponse['data'][number],
  ) => {
    trackEvent(AMPLITUDE_EVENTS.companySearchSelected, {
      'Company name': company?.name,
      'Company number': company?.number,
      'Company type': company?.company_type,
    });
    setSelectedCompany(company);
  };

  const passesTypeCheck = useCallback(() => {
    if (!selectedCompany) {
      return false;
    }
    return !selectedCompany.excluded;
  }, [selectedCompany]);

  const passesDirectorCheck = useCallback(() => {
    if (selectedPerson && selectedPerson.roles?.includes('director')) {
      return true;
    }
    return false;
  }, [selectedPerson]);

  const { checkIntroducerPermission } = useIntroducerContext();
  const cannotCreateApplication = !checkIntroducerPermission(
    'can_create_applications',
  );

  const isSubmitDisabled = useMemo(() => {
    return (
      !selectedCompany ||
      !selectedPerson ||
      !passesTypeCheck() ||
      !passesDirectorCheck() ||
      cannotCreateApplication
    );
  }, [
    passesDirectorCheck,
    passesTypeCheck,
    selectedCompany,
    selectedPerson,
    cannotCreateApplication,
  ]);

  const onError = (error: any) => {
    trackEvent(AMPLITUDE_EVENTS.initialApplicationCheckErrored, {
      'Error message': error as string,
    });

    datadogLogs.logger.error('Failed to create application', {
      createError: error as string,
    });
  };

  return (
    <form
      className={styles.container}
      onSubmit={handleSubmit(onSubmit, onError)}
    >
      <Card className={styles.formContainer}>
        <div className={styles.formField}>
          <CompanyInputUK
            onChange={setCompanyWithTracking}
            setSelectedCompany={setSelectedCompany}
            setSelectedPerson={setSelectedPersonWithDOB}
            disabled={cannotCreateApplication}
          />

          <ReintroductionSearchStatus
            isFetching={isFetchingReintroductionSearch}
            data={reintroducedPerson}
          />

          {Boolean(selectedCompany) && !passesTypeCheck() && (
            <div className={styles.criteria}>
              <CriteriaItem testId="company-type">
                Unfortunately, we don't accept applications for this company
                type.
              </CriteriaItem>
            </div>
          )}
        </div>
        {reintroducedPerson ? (
          <ReintroducedSignupFields data={reintroducedPerson} />
        ) : (
          <>
            <div className={styles.formField}>
              <DirectorSearch
                label="Applicant's name"
                onChange={setSelectedPersonWithDOB}
                companyNumber={selectedCompany?.number}
              />
              <div className={styles.criteria}>
                <CriteriaItem
                  disabled={!selectedPerson}
                  hasPassed={passesDirectorCheck()}
                  testId="director"
                >
                  The main applicant must be a director of the company and
                  prepared to provide a personal guarantee to support a loan.
                </CriteriaItem>
              </div>
            </div>

            <DateOfBirthInput
              register={register}
              errors={errors}
              selectedPerson={selectedPerson}
            />
          </>
        )}
      </Card>
      <div className={styles.footerItems}>
        <CheckBox
          {...register('privacyPolicyAgreement')}
          label="I’ve got permission from the applicant to apply on their behalf"
          className={styles.privacyPolicyCheckbox}
          disabled={cannotCreateApplication}
        />
        {errors.privacyPolicyAgreement?.message && (
          <Message
            message={errors.privacyPolicyAgreement?.message}
            type="error"
          />
        )}
        {reintroducedPerson ? (
          <Button
            disabled={cannotCreateApplication}
            loading={isSubmitting}
            type="button"
            variant="primary"
            size="large"
            onClick={submitReintroductionSignup}
          >
            Continue
          </Button>
        ) : (
          <Button
            disabled={isSubmitDisabled}
            loading={isSubmitting}
            type="submit"
            variant="primary"
            size="large"
          >
            Continue
          </Button>
        )}
        {signupError && <Message message={signupError} type="error" />}
      </div>
    </form>
  );
};
